import { CheckCircle, Clock, Edit, Archive, Beaker } from 'lucide-react';
import type { Recipe } from '../../../api/queries/recipe.queries';

interface RecipeCardProps {
  recipe: Recipe;
  onClick: () => void;
  getLangValue: (multiLang: any) => string;
}

function RecipeCard({ recipe, onClick, getLangValue }: RecipeCardProps) {
  const menuItem = typeof recipe.menuItemId === 'object' ? recipe.menuItemId : null;
  const menuItemName = menuItem ? getLangValue(menuItem.name) : 'Unknown Item';

  const getStatusIcon = () => {
    switch (recipe.status) {
      case 'published':
        return <CheckCircle className="h-3 w-3" />;
      case 'review':
        return <Clock className="h-3 w-3" />;
      case 'draft':
        return <Edit className="h-3 w-3" />;
      case 'archived':
        return <Archive className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (recipe.status) {
      case 'published':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'review':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'draft':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'archived':
        return 'bg-gray-100 text-gray-400 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const defaultFormula = recipe.formulas?.find(f => f.isDefault);
  const formulaCount = recipe.formulas?.length || 0;

  // Tea/drink emoji based on common keywords
  const getTeaEmoji = () => {
    const name = menuItemName.toLowerCase();
    if (name.includes('matcha')) return '🍵';
    if (name.includes('chocolate') || name.includes('choc')) return '🍫';
    if (name.includes('mango')) return '🥭';
    if (name.includes('peach')) return '🍑';
    if (name.includes('lychee')) return '🫐';
    if (name.includes('passion')) return '🥝';
    if (name.includes('orange') || name.includes('mandarin')) return '🍊';
    if (name.includes('pineapple')) return '🍍';
    if (name.includes('lemon') || name.includes('yogurt')) return '🍋';
    if (name.includes('thai')) return '🧋';
    if (name.includes('milk')) return '🥛';
    if (name.includes('coffee')) return '☕';
    if (name.includes('cream cheese')) return '🧀';
    return '🧋';
  };

  return (
    <button
      onClick={onClick}
      className={`
        relative flex flex-col overflow-hidden rounded-xl border bg-card text-left
        transition-all duration-150 hover:shadow-lg hover:border-primary/50 hover:-translate-y-0.5
        ${recipe.status === 'archived' ? 'opacity-60' : ''}
      `}
    >
      {/* Visual Area */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-primary/5 to-primary/20">
        {/* Tea emoji as visual */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-6xl opacity-80">{getTeaEmoji()}</span>
        </div>

        {/* Status badge */}
        <div className={`absolute top-2 right-2 flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium border ${getStatusColor()}`}>
          {getStatusIcon()}
          <span className="capitalize">{recipe.status}</span>
        </div>

        {/* Formula count badge */}
        <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-xs font-medium text-white">
          <Beaker className="h-3 w-3" />
          <span>{formulaCount} formula{formulaCount !== 1 ? 's' : ''}</span>
        </div>

        {/* Auto-switch indicator */}
        {recipe.autoSwitchEnabled && (
          <div className="absolute bottom-2 right-2 rounded-full bg-green-500/90 px-2 py-1 text-xs font-medium text-white">
            Auto
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1.5 p-3">
        <h3 className="font-semibold text-sm line-clamp-2 leading-tight">
          {menuItemName}
        </h3>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>v{recipe.version}</span>
          <span>•</span>
          <span className="capitalize">{recipe.size === 'all' ? 'All sizes' : recipe.size}</span>
        </div>

        {/* Default formula info */}
        {defaultFormula && (
          <div className="mt-1 flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center h-5 w-5 rounded bg-primary/10 text-primary text-xs font-bold">
              {defaultFormula.formulaCode}
            </span>
            <span className="text-xs text-muted-foreground truncate">
              {defaultFormula.name}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}

export default RecipeCard;
